import * as React from "react"

import { cn } from "@/utils"
import { debounce } from "lodash";

const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.ComponentProps<"textarea">
>(({ className, ...props }, ref) => {

  const [value, setValue] = React.useState(props.value || null)

  const debouncedOnChange = React.useMemo(
    () =>
      debounce(
        (
          event:
            | React.ChangeEvent<HTMLTextAreaElement>
            | {
                target: {
                  value: string;
                };
              }
        ) => {
          if (props.onChange) {
            props.onChange({
              target: {
                value: (event.target as HTMLTextAreaElement).value.replace(/<br \/>/g, '\n') // Replace <br /> with newline
              }
            } as React.ChangeEvent<HTMLTextAreaElement>);
          }
        },
        500
      ),
    [props.onChange]
  );

  React.useEffect(() => {
    // Cleanup the debounce on unmount to prevent memory leaks
    return () => {
      debouncedOnChange.cancel();
    };
  }, [debouncedOnChange]);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value?.replace(/<br \/>/g, '\n');
    setValue(newValue); // Update the UI immediately
    debouncedOnChange(event); // Debounce the side-effect
  };

  return (
    <textarea
      className={cn(
        "flex min-h-[80px] w-full rounded-none border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className
      )}
      ref={ref}
      {...props}
      value={(value) as string}
      onChange={handleChange}
    />
  )
})
Textarea.displayName = "Textarea"

export { Textarea }
